import React, { useContext } from "react"; // Add useContext
import { motion } from "framer-motion";
import { Lock, AlertCircle } from "lucide-react";
import "./verification-status.css";
import Loading from "../loading/Loading";
import { GlobalContext } from "../../GlobalContext"; // Import GlobalContext
import { useNavigate } from "react-router";

const VerificationStatus = () => {
  const navigate = useNavigate();
  const { userData, isLoading, apiError } = useContext(GlobalContext); // Access data from GlobalContext

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
      },
    },
  };

  const thumbprintVariants = {
    hidden: { opacity: 0, rotate: -10 },
    visible: (i) => ({
      opacity: 0.7,
      rotate: 0,
      transition: {
        delay: 0.2 + i * 0.1,
        type: "spring",
        stiffness: 200,
        damping: 20,
      },
    }),
  };

  const buttonVariants = {
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95, transition: { duration: 0.1 } },
  };

  if (isLoading) {
    return (
      <div className="verification-status-container">
        <Loading />
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="verification-status-container">
        <div className="error">{apiError}</div>
      </div>
    );
  }

  return (
    <motion.div
      className="verification-status-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Request Re-verification Notification */}
      <motion.div className="reverification-notification" variants={itemVariants}>
        <div className="notification-content">
          <div className="notification-left">
            <Lock size={26} color="#3A3F6A" />
            <span>Full access to all features will be granted upon verification</span>
          </div>
          <motion.a
            onClick={() => navigate("/contact-form")}
            className="request-reverification-button"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            Request re-verification
          </motion.a>
        </div>
      </motion.div>

      {/* Welcome Card */}
      <motion.div className="welcome-card" variants={itemVariants}>
        <motion.div
          className="welcome-text"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Welcome 👋, Dr. {userData.firstName}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Stay on top of your test orders and patient results.
          </motion.p>
        </motion.div>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
          <motion.img
            key={`thumbprint-${i}`}
            src={`${process.env.PUBLIC_URL}/assets/thumbprint.png`}
            alt="Thumbprint"
            className={`thumbprint-image thumbprint-image-${Math.min(i, 6)}`}
            variants={thumbprintVariants}
            custom={i}
            initial="hidden"
            animate="visible"
          />
        ))}
      </motion.div>

      {/* Verification Details */}
      <motion.div className="verification-details" variants={itemVariants}>
        {/* Left Side: Profile Info */}
        <div className="profile-info">
          <div className="profile-pic">
            {userData.profilePic ? (
              <img
                src={userData.profilePic}
                alt="Profile"
                className="profile-pic-img"
                onError={() => {}}
              />
            ) : (
              userData.firstName.charAt(0).toUpperCase()
            )}
          </div>
          <span className="full-name">
            {userData.firstName} {userData.lastName}
          </span>
          <span className="email">{userData.email}</span>
          <span className="label">Medical license</span>
          <span className={`status status-${userData.verificationStatus.toLowerCase()}`}>
            {userData.verificationStatus}
          </span>
        </div>

        {/* Right Side: Verification Info */}
        <div className="verification-info">
          <div className="info-section">
            <div className="subheader">
              🛑
              {/* <AlertCircle size={26} color="#D61E1E" /> */}
              <span>Verification in Progress</span>
            </div>
            <p className="info-text">
              Thank you for signing up! Your verification is currently in progress. We’ll notify you once it’s complete.
            </p>
          </div>
          <div className="info-section">
            <div className="subheader why-verification">Why verification?</div>
            <p className="info-text">
              To ensure the highest standards of care, we verify all providers to confirm their credentials and maintain a secure platform for patients and professionals alike.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default VerificationStatus;